import React, { useState } from 'react';

import { PopOver } from '@bringg/react-components';
import classNames from 'classnames';
import { formatFloatAsPercent } from '@bringg-frontend/utils';
import { TypographyColor, TypographyHelper } from '@bringg-frontend/ui';

import { CapacityUtilization } from '../../../capacity-utilization.types';
import { PlannedDeliveryWindowsEventType } from '../../planned-delivery-windows-calendar';
import PlannedDeliveryWindowsPopoverContent from '../popover-content/planned-delivery-windows-popover-content';
import OldPlannedDeliveryWindowsPopoverContent from '../popover-content/old-planned-delivery-windows-popover-content';
import PlannedDeliveryWindowsEventRelationsName from './planned-delivery-windows-event-relations-name';
import { Cutoff } from '../../../cutoff/cutoff';
import { DELIVERY_SLOTS_UI_IMPROVEMENT_FF, ENABLE_FILL_RATIO_DATA } from '../../../constants';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';
import { selectionService } from 'bringg-web/services/selection/selection-service';
import { getTaskCapacityLevel } from '../../../services/task-capacity-color';

import styles from './planned-delivery-windows-event.module.scss';

interface Props {
	title: string;
	event: PlannedDeliveryWindowsEventType;
	openModalWithPlannedDeliveryWindow: (plannedDeliveryWindowId: number) => void;
	openRecurrenceTypeModalWithDeleteMode: (id: number) => void;
	selectedEventsIdsLength: number;
	capacityUtilization: CapacityUtilization;
	timezone: string;
	isCapacityAppInstalled: boolean;
	capacityTeamIds: number[];
}

const PlannedDeliveryWindowsEvent: React.FC<Props> = ({
	event,
	title,
	openModalWithPlannedDeliveryWindow,
	openRecurrenceTypeModalWithDeleteMode,
	capacityUtilization,
	timezone,
	selectedEventsIdsLength,
	isCapacityAppInstalled,
	capacityTeamIds
}: Props) => {
	const [popoverOpen, setPopoverOpen] = useState(false);
	const isPDWMultiSelectEnabled = useHasFeatureFlag('pdw_multi_select');
	const isDeliverySlotsUiImprovementEnabled = useHasFeatureFlag(DELIVERY_SLOTS_UI_IMPROVEMENT_FF);
	const isPDWCapacityUtilizationV2 = useHasFeatureFlag(ENABLE_FILL_RATIO_DATA);
	const hasTaskCapacityAccess = isCapacityAppInstalled && capacityTeamIds.includes(event.team_id);

	const hasFillRatioData = Boolean(capacityUtilization.fillRatioData?.fillRatioBy);

	const maxTaskCapacity = event.max_task_capacity;
	const showTaskCapacity = hasTaskCapacityAccess && maxTaskCapacity != null;
	const usedTaskCapacity = capacityUtilization.additionalAttributes?.usedTaskCapacity ?? 0;
	const taskCapacityRatio = maxTaskCapacity > 0 ? usedTaskCapacity / maxTaskCapacity : 1;
	const taskCapacityLevel = showTaskCapacity ? getTaskCapacityLevel(taskCapacityRatio) : null;
	const displayedPercent = showTaskCapacity ? taskCapacityRatio : capacityUtilization.fillRatio;

	const onClickOnEvent = clickEvent => {
		if ((isPDWMultiSelectEnabled && selectionService.isMultiSelect(clickEvent)) || selectedEventsIdsLength > 1) {
			return;
		}

		openModalWithPlannedDeliveryWindow(event.id);
		clickEvent.stopPropagation();
	};

	const useV2Popover = isPDWCapacityUtilizationV2 && hasFillRatioData;
	const PopoverContent = useV2Popover
		? PlannedDeliveryWindowsPopoverContent
		: OldPlannedDeliveryWindowsPopoverContent;

	return (
		<PopOver
			content={
				<PopoverContent
					title={title}
					event={event}
					setPopoverOpen={setPopoverOpen}
					openModalWithPlannedDeliveryWindowSlot={openModalWithPlannedDeliveryWindow}
					openRecurrenceTypeModalWithDeleteMode={openRecurrenceTypeModalWithDeleteMode}
					capacityUtilization={capacityUtilization}
					timezone={timezone}
				/>
			}
			trigger="hover"
			mouseEnterDelay={1}
			showArrow={false}
			placement="right"
			open={popoverOpen}
			onOpenChange={newOpen => {
				if (newOpen && isPDWCapacityUtilizationV2 && !hasFillRatioData) return;
				setPopoverOpen(newOpen);
			}}
		>
			<div data-test-id={`pdw-id-${event.id}`} className={styles.eventContentItem} onClick={onClickOnEvent}>
				<div className={styles.topRow}>
					<TypographyHelper tag="span" color={TypographyColor.Inverse}>
						{formatFloatAsPercent(displayedPercent)}
					</TypographyHelper>
					{showTaskCapacity && (
						<TypographyHelper
							tag="span"
							color={TypographyColor.Inverse}
							data-test-id="task-capacity-badge"
							className={classNames(styles.taskCapacityBadge, styles[taskCapacityLevel!])}
						>
							{usedTaskCapacity}/{maxTaskCapacity}
						</TypographyHelper>
					)}
				</div>
				<span className={styles.title}>{title}</span>
				{event.cutoff !== 0 && (
					<Cutoff startWindowTime={event.start} cutoff={event.cutoff} timezone={timezone} />
				)}
				{isDeliverySlotsUiImprovementEnabled && <PlannedDeliveryWindowsEventRelationsName event={event} />}
			</div>
		</PopOver>
	);
};

export default React.memo(PlannedDeliveryWindowsEvent);
